import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { eur } from "@/components/LineItemsEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Send, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

// Envoi d'un devis par e-mail (PDF joint + lien espace client pour accepter/signer).
export default function QuoteSendDialog({ quote, onClose, onSent }) {
  const [client, setClient] = useState(null);
  const [to, setTo] = useState("");
  const [msg, setMsg] = useState("");
  const [copy, setCopy] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmAgain, setConfirmAgain] = useState(false);

  useEffect(() => {
    if (!quote) { return; }
    setConfirmAgain(false); setCopy(true);
    api.get(`/clients/${quote.client_id}`).then((r) => {
      const c = r.data?.client || r.data || {};
      setClient(c); setTo(c.email || "");
      const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company || "";
      setMsg(`Bonjour${name ? ` ${name}` : ""},\n\nVeuillez trouver ci-joint notre devis ${quote.number} d'un montant de ${eur(quote.total)}.\nVous pouvez le consulter et l'accepter en ligne grâce au lien ci-dessous.\n\nN'hésitez pas à nous contacter pour toute question.\n\nCordialement,`);
    }).catch(() => setClient({}));
  }, [quote]);

  const missing = client && !client.email;
  const alreadySent = (quote?.email_history || []).some((h) => h.status === "envoye");

  const send = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to.trim())) return toast.error("Saisissez une adresse e-mail valide");
    if (alreadySent && !confirmAgain) { setConfirmAgain(true); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${quote.id}/send`, { to_email: to.trim(), message: msg, copy_to_owner: copy, save_email_to_client: !client?.email || client.email !== to.trim(), force: confirmAgain });
      toast.success(`Devis envoyé à ${data.sent_to}`);
      if (data.copy_error) toast.warning(data.copy_error);
      onSent(data.quote); onClose();
    } catch (e) {
      if (e.response?.status === 409) { setConfirmAgain(true); } else { toast.error(formatApiError(e.response?.data?.detail)); }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!quote} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[92vh] overflow-y-auto" data-testid="quote-send-dialog">
        <DialogHeader><DialogTitle className="text-slate-900">Envoyer le devis {quote?.number}</DialogTitle><DialogDescription>Le PDF est joint, avec le lien pour accepter le devis en ligne.</DialogDescription></DialogHeader>
        {missing && <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="quote-send-no-email"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />Ce client n'a pas d'adresse e-mail. Saisissez-la ci-dessous : elle sera enregistrée sur sa fiche.</div>}
        <div className="space-y-1.5"><Label className="text-xs text-slate-500">Destinataire</Label><Input type="email" inputMode="email" value={to} onChange={(e) => setTo(e.target.value)} data-testid="quote-send-to" placeholder="client@exemple.fr" /></div>
        <div className="space-y-1.5"><Label className="text-xs text-slate-500">Message</Label><Textarea rows={7} value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="quote-send-message" /></div>
        <label className="flex items-center gap-2 text-sm text-slate-700"><Checkbox checked={copy} onCheckedChange={(v) => setCopy(!!v)} data-testid="quote-send-copy" />M'envoyer une copie</label>
        {(quote?.email_history || []).length > 0 && (
          <div className="rounded-lg border border-slate-200 p-3" data-testid="quote-send-history">
            <div className="text-xs font-semibold text-slate-500 mb-1">Historique d'envoi</div>
            {[...quote.email_history].reverse().map((h, i) => (
              <div key={i} className="text-xs text-slate-600 flex items-start gap-1.5 py-0.5">{h.status === "envoye" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-red-600 shrink-0" />}<span className="break-words">{fmt(h.at)} · {h.to} · {h.status === "envoye" ? `Envoyé${h.cc?.length && !h.copy_error ? ` (copie ${h.cc[0]})` : ""}${h.copy_error ? ` — ${h.copy_error}` : ""}` : `Échec${h.error ? ` : ${h.error}` : ""}`}</span></div>
            ))}
          </div>
        )}
        {confirmAgain && <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="quote-send-confirm-again">Ce devis a déjà été envoyé. Appuyez à nouveau sur « Renvoyer » pour confirmer.</div>}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="quote-send-cancel">Annuler</Button>
          <Button onClick={send} disabled={busy || !client} data-testid="quote-send-submit" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}{confirmAgain ? "Renvoyer" : "Envoyer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
